import { db, storage } from "../../firebase";
import {
  Timestamp,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { formatMonthYear } from "../../utils/formatDate";

export const createNewArchive = async ({
  data,
  documents,
  collectionName = "Archive",
}) => {
  if (!data?.publishedBy) {
    throw new Error("Published By is undefined");
  }
  if (!data?.categoryId) {
    throw new Error("Category ID is undefined");
  }
  if (!data?.timestamp) {
    throw new Error("Timestamp is undefined");
  }
  if (
    documents.some(
      (doc) =>
        !doc.slug || !doc.title || !doc.author || !doc.pageNo || !doc.pdfFile
    )
  ) {
    throw new Error(
      "All document fields (slug, title, author, pageNo, pdfFile) are required"
    );
  }

  const monthYear = formatMonthYear(data.timestamp);
  const id =
    data?.id ||
    `${data.publishedBy.replace(/\s+/g, "-").toLowerCase()}-${monthYear}`;

  // Create document reference
  const firestoreRef = doc(db, collectionName, id);

  try {
    // Save archive document
    await setDoc(firestoreRef, {
      ...data,
      id,
      productType: "Archive", // Hardcode productType
      timestamp: data?.timestamp || Timestamp.now(),
      monthYear, // Store monthYear for querying
    });

    // Save documents subcollection
    for (const docData of documents) {
      // Upload PDF to Archive/<slug>.pdf
      const pdfRef = ref(storage, `${collectionName}/${docData.slug}.pdf`);
      await uploadBytes(pdfRef, docData.pdfFile);
      const pdfUrl = await getDownloadURL(pdfRef);

      // Save document metadata to Firestore (exclude pdfFile and timestamp)
      const docRef = doc(db, `${collectionName}/${id}/Documents`, docData.slug);
      await setDoc(docRef, {
        slug: docData.slug,
        title: docData.title,
        author: docData.author,
        pageNo: docData.pageNo,
        id: docData.slug,
        pdfUrl,
      });
    }
  } catch (error) {
    console.error("Firestore Write Error:", error);
    throw new Error(`Failed to create archive: ${error.message}`);
  }
};

export const updateArchive = async ({
  data,
  documents,
  collectionName = "Archive",
}) => {
  if (!data?.id) {
    throw new Error("ID is undefined for update");
  }
  if (!data?.publishedBy) {
    throw new Error("Published By is undefined");
  }
  if (!data?.categoryId) {
    throw new Error("Category ID is undefined");
  }
  if (!data?.timestamp) {
    throw new Error("Timestamp is undefined");
  }
  if (
    documents.some(
      (doc) => !doc.slug || !doc.title || !doc.author || !doc.pageNo
    )
  ) {
    throw new Error(
      "All document fields (slug, title, author, pageNo) are required"
    );
  }

  const monthYear = formatMonthYear(data.timestamp);
  const firestoreRef = doc(db, collectionName, data.id);

  try {
    await updateDoc(firestoreRef, {
      ...data,
      productType: "Archive", // Hardcode productType
      timestamp: data?.timestamp || Timestamp.now(),
      monthYear,
    });

    // Update documents subcollection
    const docsCollection = collection(
      db,
      `${collectionName}/${data.id}/Documents`
    );
    const existingDocs = await getDocs(docsCollection);
    for (const docSnap of existingDocs.docs) {
      await deleteDoc(
        doc(db, `${collectionName}/${data.id}/Documents`, docSnap.id)
      );
      try {
        // Delete old PDF from Archive/<slug>.pdf
        await deleteObject(ref(storage, `${collectionName}/${docSnap.id}.pdf`));
      } catch (error) {
        if (error.code !== "storage/object-not-found") {
          throw error;
        }
      }
    }

    for (const docData of documents) {
      // Upload new PDF if provided
      let pdfUrl = docData.pdfUrl;
      if (docData.pdfFile) {
        const pdfRef = ref(storage, `${collectionName}/${docData.slug}.pdf`);
        await uploadBytes(pdfRef, docData.pdfFile);
        pdfUrl = await getDownloadURL(pdfRef);
      }
      // Save document metadata to Firestore (exclude pdfFile and timestamp)
      const docRef = doc(
        db,
        `${collectionName}/${data.id}/Documents`,
        docData.slug
      );
      await setDoc(docRef, {
        slug: docData.slug,
        title: docData.title,
        author: docData.author,
        pageNo: docData.pageNo,
        id: docData.slug,
        pdfUrl,
      });
    }
  } catch (error) {
    console.error("Firestore Update Error:", error);
    throw new Error(`Failed to update archive: ${error.message}`);
  }
};

export const deleteArchive = async (
  id,
  monthYear,
  collectionName = "Archive"
) => {
  if (!id) {
    throw new Error("Id is required");
  }
  if (!monthYear) {
    throw new Error("Month and year are required");
  }

  try {
    // Delete documents subcollection and their PDFs
    const docsCollection = collection(db, `${collectionName}/${id}/Documents`);
    const docsSnapshot = await getDocs(docsCollection);
    for (const docSnap of docsSnapshot.docs) {
      await deleteDoc(doc(db, `${collectionName}/${id}/Documents`, docSnap.id));
      try {
        // Delete PDF from Archive/<slug>.pdf
        await deleteObject(ref(storage, `${collectionName}/${docSnap.id}.pdf`));
      } catch (error) {
        if (error.code !== "storage/object-not-found") {
          throw error;
        }
      }
    }

    // Delete archive document
    await deleteDoc(doc(db, collectionName, id));
  } catch (error) {
    console.error("Firestore Delete Error:", error);
    throw new Error(`Failed to delete archive: ${error.message}`);
  }
};
